import { Stack, styled, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { DataTable } from "@/components/custom/data-table";
import CustomSearchInput from "@/components/custom/custom-search-input";
import CustomSelectInput from "@/components/custom/custom-select-input";
import CustomPagination from "@/components/custom/custom-pagination";
import CustomSimpleCard from "@/components/custom/custom-simple-card";
import { useMemo } from "react";
import { useComplaints } from "@/hooks/use-complaints";
import { useComplaintStats } from "@/hooks/use-complaint-stats";
import { formatCurrency } from "@/utils/format-currency";
import { ComplaintColumns } from "./_components/complaint-columns";
import { ComplaintsSummary, complaintTypeOptions } from "../data";

const SectionRoot = styled("section")(({ theme }) => ({
  margin: theme.spacing(2),
}));

const Card = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: "16px 20px",
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

export const Complaints = () => {
  const [searchParams] = useSearchParams();

  const searchValue = searchParams.get("search") || "";
  const currentPage = Number(searchParams.get("page") || 0);
  const pageSize = Number(searchParams.get("size") || 10);
  const disputeType = searchParams.get("disputeType");

  const queryParams = {
    page: currentPage,
    size: pageSize,
    disputeType: disputeType || undefined,
    search: searchValue,
  };

  const {
    data: complaints,
    isLoading,
    isError,
  } = useComplaints(true, queryParams);

  const { stats, isLoading: isStatsLoading } = useComplaintStats();

  const columns = useMemo(() => ComplaintColumns, []);

  return (
    <SectionRoot>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {ComplaintsSummary.map(({ title, objKeyRepr, icon, isCurrency }, idx) => (
            <CustomSimpleCard
              key={`${objKeyRepr}-${idx}`}
              title={title}
              icon={icon}
              isLoading={isStatsLoading}
            >
              <Typography variant="h6">
                {isCurrency
                  ? formatCurrency(stats?.[objKeyRepr] ?? 0)
                  : (stats?.[objKeyRepr] ?? 0)}
              </Typography>
            </CustomSimpleCard>
          ))}
        </Stack>

        <Card>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <CustomSearchInput paramKey="search" placeholder="Search complaints..." />
            <CustomSelectInput
              paramKey="disputeType"
              label="Type"
              placeholder="All Types"
              options={complaintTypeOptions}
            />
          </Stack>

          <DataTable
            data={complaints?.data?.data ?? []}
            columns={columns}
            isLoading={isLoading}
            isError={isError}
            noDataMessage="No complaints found."
          />

          <CustomPagination
            total={complaints?.data?.totalCount ?? 0}
            perPageOptions={[5, 10, 20, 50, 100]}
          />
        </Card>
      </Stack>
    </SectionRoot>
  );
};
